import type { Category, CitationGraph, ContentAsset, FeedItem, Paper, PaperExplanationRequest, PaperRequestReceipt } from '../../../src/contracts/types.ts';
import type { CitationGraphEmptyReason } from '../../../src/core/contentDisplay.ts';
export type { FeedItem } from '../../../src/contracts/types.ts';

/**
 * 公開 API クライアント。
 *
 * 型は src/contracts/types.ts をそのまま共有する。
 * iOS / Android へ展開する際も同じ契約を使う。
 */

export interface CategorySummary extends Category {
  paper_count?: number;
  name_en?: string;
  order_no?: number;
}

export interface PaperDetailResponse {
  paper: Paper & { slug: string; headline: string; significance: string };
  category: { code: string; name: string; accent: string; summary: string } | null;
  assets: Array<ContentAsset & { locked?: boolean }>;
  /**
   * 引用グラフはこの応答には載らない（常に null）。独立して閲覧境界を切り替える面なので、
   * 載せると論文応答まで閲覧者依存になりキャッシュが効かなくなる。
   * 画面は fetchGraph() を使う。
   */
  graph: CitationGraph | null;
  /** 'open' なら誰でも読める。'paid' は契約が要る。 */
  gate?: 'open' | 'paid';
  /** true のとき、assets の本文は落とされている（存在はする）。 */
  locked?: boolean;
}

/** 会員の状態。画面はこれ1本で「ログイン中か」「有料か」を判断する。 */
export interface MeResponse {
  authenticated: boolean;
  monetizationEnabled: boolean;
  email?: string;
  emailVerified?: boolean;
  tier: 'anonymous' | 'free' | 'paid';
  phase?: 'none' | 'active' | 'grace' | 'lapsed' | 'comped';
  comped?: boolean;
  currentPeriodEnd?: number | null;
  cancelAtPeriodEnd?: boolean;
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, {
    headers: { accept: 'application/json' },
    // 同一オリジンなので既定でも送られるが、意図を明示しておく。
    credentials: 'same-origin',
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    let message = `通信に失敗しました（${response.status}）`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      /* 本文が JSON でない場合は既定の文言を使う */
    }
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

export function fetchCategories(signal?: AbortSignal): Promise<{ categories: CategorySummary[] }> {
  return request('/v1/categories', signal);
}

export function fetchFeed(
  params: { date?: string; category?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<{ items: FeedItem[]; limit: number; offset: number; hasMore: boolean }> {
  const query = new URLSearchParams();
  if (params.date) query.set('date', params.date);
  if (params.category) query.set('category', params.category);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.offset !== undefined) query.set('offset', String(params.offset));
  return request(`/v1/feed?${query.toString()}`, signal);
}

export function fetchPaper(idOrSlug: string, signal?: AbortSignal): Promise<PaperDetailResponse> {
  return request(`/v1/papers/${encodeURIComponent(idOrSlug)}`, signal);
}

/**
 * 引用グラフが無いときの理由。
 *
 * 「引用 0 件（提供元に登録が無い）」と「全文を取得できていない」は別の事象なのに、
 * 画面では混同されやすい。Worker は graph: null に理由を添えて返し、画面は理由ごとに文言を分ける。
 * 文言とチップを決めるのは `citationGraphEmptyState`（src/core/contentDisplay.ts）で、
 * 理由の定義もそこに置いてある。ここで別の union を書くと二重定義になるので別名にする。
 */
export type GraphEmptyReason = CitationGraphEmptyReason;

export interface GraphResponse {
  graph: CitationGraph | null;
  reason?: GraphEmptyReason;
  /** graph が null のときに、提供元からの取得がそもそも一部失敗していたかどうか。 */
  truncated?: { references: boolean; citedBy: boolean };
}

export function fetchGraph(
  idOrSlug: string,
  params: { direction?: string; internalOnly?: boolean; maxNodes?: number },
  signal?: AbortSignal,
): Promise<GraphResponse> {
  const query = new URLSearchParams();
  if (params.direction && params.direction !== 'all') query.set('direction', params.direction);
  if (params.internalOnly) query.set('internalOnly', '1');
  if (params.maxNodes) query.set('maxNodes', String(params.maxNodes));
  return request(`/v1/papers/${encodeURIComponent(idOrSlug)}/graph?${query.toString()}`, signal);
}

export function search(
  query: string,
  signal?: AbortSignal,
): Promise<{
  query: string;
  items: Array<{
    paperId: string;
    slug: string;
    title: string;
    titleJa?: string;
    headline?: string;
    categoryCode: string;
    categoryName: string;
    categoryAccent: string;
    runDate: string;
    year?: number;
  }>;
}> {
  return request(`/v1/search?q=${encodeURIComponent(query)}`, signal);
}


/**
 * 会員向けの POST。
 *
 * `sec-fetch-site` はブラウザが付けるので、こちらでは触らない
 * （偽装できない値であることに意味がある）。
 * サーバ側は Origin も見るため、通常の fetch であればそのまま通る。
 */
async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  let parsed: { message?: string; error?: string } = {};
  try {
    parsed = (await response.json()) as { message?: string; error?: string };
  } catch {
    /* 本文が無い応答もある */
  }
  if (!response.ok) {
    throw new ApiError(response.status, parsed.message ?? `通信に失敗しました（${response.status}）`);
  }
  return parsed as T;
}

export function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return request('/v1/me', signal);
}

export function signup(email: string, password: string): Promise<{ ok: boolean; message: string }> {
  return post('/v1/auth/signup', { email, password });
}

export function login(email: string, password: string): Promise<{ ok: boolean }> {
  return post('/v1/auth/login', { email, password });
}

export function logout(): Promise<{ ok: boolean }> {
  return post('/v1/auth/logout');
}

export function verifyEmail(token: string): Promise<{ ok: boolean; message: string }> {
  return post('/v1/auth/verify', { token });
}

export function requestPasswordReset(email: string): Promise<{ ok: boolean; message: string }> {
  return post('/v1/auth/reset-request', { email });
}

export function resetPassword(token: string, password: string): Promise<{ ok: boolean; message: string }> {
  return post('/v1/auth/reset', { token, password });
}

export function fetchBillingStatus(
  signal?: AbortSignal,
): Promise<{ available: boolean; disabled?: boolean; missingConfig?: string[] }> {
  return request('/v1/billing/status', signal);
}

/** 申し込み。戻り値の URL へ **トップレベル遷移**すること。 */
export function startCheckout(): Promise<{ url: string }> {
  return post('/v1/billing/checkout');
}

/** 契約の管理（支払方法の更新・請求履歴・解約）。 */
export function openBillingPortal(): Promise<{ url: string }> {
  return post('/v1/billing/portal');
}


export interface SyncPayload {
  bookmarks: Array<{
    paperId: string;
    slug: string;
    title: string;
    headline?: string;
    categoryName?: string;
    addedAt: number;
    deletedAt?: number | null;
    updatedAt?: number;
  }>;
  history: Array<{
    paperId: string;
    slug: string;
    title: string;
    headline?: string;
    categoryName?: string;
    viewedAt: number;
  }>;
  preferences: {
    categories: string[];
    level: string;
    medium: string;
    graphDirection: string;
  } | null;
}

/** 端末内の内容を送り、突き合わせた結果を受け取る。 */
export function pushSync(payload: Partial<SyncPayload>): Promise<SyncPayload> {
  return post('/v1/me/sync', payload);
}

export function pullSync(signal?: AbortSignal): Promise<SyncPayload> {
  return request('/v1/me/sync', signal);
}

/** 退会。**元に戻せません。** 呼ぶ前に必ず確認を取ること。 */
export function deleteAccount(): Promise<{ ok: boolean; subscription: string }> {
  return post('/v1/me/delete', { confirm: true });
}


export interface LegalResponse {
  tokushoho: { rows: Array<{ label: string; value: string }>; complete: boolean };
  terms: { sections: Array<{ heading: string; body: string[] }>; updatedAt: string };
  privacy: { sections: Array<{ heading: string; body: string[] }>; updatedAt: string; complete: boolean };
}

export function fetchLegal(signal?: AbortSignal): Promise<LegalResponse> {
  return request('/v1/legal', signal);
}

export function fetchPaperRequestAvailability(): Promise<{ available: boolean }> {
  return request('/v1/paper-requests');
}

export function submitPaperRequest(body: PaperExplanationRequest): Promise<PaperRequestReceipt> {
  return post('/v1/paper-requests', body);
}
